import { ref, computed, getCurrentInstance } from "vue";
import api from "@/services/api";
import debounce from "lodash/debounce";
import Swal from "sweetalert2";

export function useActions(resourceApi) {
  const { proxy } = getCurrentInstance();

  const items = ref([]);
  const currentPage = ref(1);
  const lastPage = ref(1);
  const totalItems = ref(0);
  const itemsPerPage = ref(6);
  const loading = ref(false);
  const error = ref(null);
  const fieldErrors = ref({});
  const sortColumn = ref("updated_at");
  const filterType = ref("");
  const sortOrder = ref("desc");
  const search = ref("");
  const onlyDeleted = ref(false);
  const cache = new Map();
  const additionalParams = ref({});
  const message = ref("");
  const totalPages = ref(0);

  const filteredItems = computed(() => items.value || []);

  const setAdditionalParams = (params) => {
    additionalParams.value = params;
  };

  const fetchItems = debounce(
    async (page = currentPage.value, ignoreCache = false) => {
      const params = {
        page,
        search: search.value,
        per_page: itemsPerPage.value,
        sort_column: sortColumn.value,
        filterType: filterType.value,
        sort_order: sortOrder.value,
        only_deleted: onlyDeleted.value,
        ...additionalParams.value,
      };

      const cacheKey = JSON.stringify(params);

      if (!ignoreCache && cache.has(cacheKey)) {
        const cachedData = cache.get(cacheKey);
        if (cachedData) {
          items.value = cachedData.items || [];
          currentPage.value = cachedData.currentPage || 1;
          lastPage.value = cachedData.lastPage || 1;
          totalItems.value = cachedData.totalItems || 0;
          totalPages.value = cachedData.lastPage || 1;
          return;
        }
      }

      try {
        loading.value = true;
        error.value = null;

        const response = await api.get(`/${resourceApi}`, { params });

        if (!response.data || typeof response.data !== "object") {
          throw new Error("La API no devolvió una respuesta válida.");
        }

        if (!response.data.data || !Array.isArray(response.data.data)) {
          items.value = [];
        } else {
          items.value = response.data;
        }

        currentPage.value = response.data.pagination?.current_page || 1;
        lastPage.value = response.data.pagination?.last_page || 1;
        totalItems.value = response.data.pagination?.total || 0;
        totalPages.value = response.data.pagination?.last_page || 1;

        cache.set(cacheKey, {
          items: items.value,
          currentPage: currentPage.value,
          lastPage: lastPage.value,
          totalItems: totalItems.value,
        });

        if (items.value.length > 0) {
          localStorage.setItem(resourceApi, JSON.stringify(response.data));
        }
      } catch (err) {

        handleApiError(err);
      } finally {
        loading.value = false;
      }
    },
    300 // 📌 Debounce de 300ms para evitar múltiples llamadas innecesarias
  );

  const reloadRecords = async () => {
    await fetchItems(currentPage.value);
  };

  const addModel = async (itemData, resourceApiOverride = resourceApi) => {
    try {
      error.value = null;
      fieldErrors.value = {};
      const response = await api.post(`/${resourceApiOverride}`, itemData);

      if (!Array.isArray(items.value)) {
        items.value = [];
      }

      if (Array.isArray(response.data.data)) {
        items.value.unshift(...response.data.data);
      } else {
        items.value.unshift(response.data.data);
      }

      cache.clear();
      await reloadRecords();
      message.value = response.data.message;
      // Actualizar localStorage
      localStorage.setItem(resourceApiOverride, JSON.stringify(items.value));
      return response;

    } catch (err) {
      handleApiError(err);
      return null;
    }
  };

  const editModel = async (itemData) => {
    try {
      error.value = null;
      fieldErrors.value = {};
      const response = await api.put(`/${resourceApi}/${itemData.id}`, itemData);

      // Verificar que `items.value` es un array
      if (!Array.isArray(items.value)) {
        items.value = [];
      }

      const index = items.value.findIndex((item) => item.id === itemData.id);

      // Verificar si `response.data.data` es un array o un objeto
      if (index !== -1) {
        if (Array.isArray(response.data.data)) {
          console.warn("La API devolvió un array en lugar de un objeto en editModel.");
          items.value.splice(index, 1, ...response.data.data); // Agrega los elementos del array
        } else {
          items.value.splice(index, 1, response.data.data); // Agrega un solo objeto
        }
      }

      cache.clear();
      await reloadRecords();
      message.value = response.data.message;

      // Guardar en localStorage
      localStorage.setItem(resourceApi, JSON.stringify(items.value));

      return response;
    } catch (err) {
      handleApiError(err);
      return null;
    }
  };


  const deleteRecord = async (itemId) => {
    try {
      error.value = null;
      const response = await api.delete(`/${resourceApi}/${itemId}`);

      // Verificar que `items.value` sea un array antes de usar `.filter()`
      if (!Array.isArray(items.value)) {
        items.value = [];
      }

      // Filtrar el array correctamente
      items.value = items.value.filter((item) => item.id !== itemId);

      cache.clear();
      await reloadRecords();

      // Validar que `message` existe antes de asignarlo
      message.value = response.data?.message || "Registro eliminado correctamente.";

      // Guardar en localStorage
      localStorage.setItem(resourceApi, JSON.stringify(items.value));

      return response;
    } catch (err) {
      handleApiError(err);
      return null;
    }
  };

  const showRecord = async (itemId, resourceApiOverride = resourceApi) => {
    try {
      error.value = null;
      loading.value = true;

      if (!itemId || !itemId.id) {
        throw new Error("El ID del registro no es válido.");
      }

      const response = await api.get(`/${resourceApiOverride}/${itemId.id}`);

      // Validar si `data.data` existe en la respuesta
      if (!response.data || !response.data.data) {
        throw new Error("No se encontraron datos para el registro solicitado.");
      }

      const selectedItem = response.data.data;

      // Guardar el registro seleccionado en localStorage
      localStorage.setItem(
        `${resourceApiOverride}_selected`,
        JSON.stringify(selectedItem)
      );

      return selectedItem;
    } catch (err) {
      handleApiError(err);
      return null; // Retorna `null` en caso de error para evitar valores indefinidos
    } finally {
      loading.value = false;
    }
  };

  const getRecord = (resourceApiOverride = resourceApi) => {
    try {
      const selectedItem = localStorage.getItem(`${resourceApiOverride}_selected`);
      return selectedItem ? JSON.parse(selectedItem) : null;
    } catch (err) {
      console.error("Error al recuperar el registro desde localStorage:", err);
      return null;
    }
  };


  const confirmDelete = async (item, displayFieldValue, displayModelValue) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      html: `¿Quieres eliminar el <b>${displayFieldValue}</b> en ${displayModelValue}?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, ¡elimínalo!",
      customClass: {
        confirmButton: "btn bg-gradient-success",
        cancelButton: "btn bg-gradient-danger",
      },
      buttonsStyling: false,
    });

    if (result.isConfirmed) {
      const response = await deleteRecord(item.id);
      if (response && (response.status === 200 || response.status === 201)) {
        Swal.fire({
          title: "¡Eliminado!",
          text: `El ${displayModelValue} se ha sido eliminado.`,
          icon: "success",
          customClass: {
            confirmButton: "btn bg-gradient-success",
          },
          buttonsStyling: false,
        });
      }
    }
  };

  const restoreRecord = async (itemId) => {
    try {
      error.value = null;
      const response = await api.post(`/${resourceApi}/${itemId}/restore`);
      await reloadRecords();
      return response;
    } catch (err) {
      handleApiError(err);
    }
  };

  const changePage = async (page) => {
    currentPage.value = page;
    await fetchItems(page);
  };

  const applyFilter = ({
    search: searchQuery,
    selectedFilter,
    filters = {},
  }) => {
    search.value = searchQuery;
    sortColumn.value = selectedFilter;
    filterType.value = selectedFilter;

    setAdditionalParams({
      ...filters,
    });

    fetchItems(1, searchQuery, selectedFilter).then(() => {
      console.log(`Número de páginas: ${totalPages.value}`);
    });
  };

  const updateItemsPerPage = (newItemsPerPage) => {
    itemsPerPage.value = newItemsPerPage;
    fetchItems(1);
  };

  const updateShowDeleted = (show) => {
    onlyDeleted.value = show;
    fetchItems(1);
  };

  const handleApiError = (err) => {
    if (err.response) {
      switch (err.response.status) {
        case 422:
          error.value = err.response.data.error || err.response.data.message;
          fieldErrors.value = err.response.data.errors || {};
          break;
        case 404:
          error.value = "Recurso no encontrado.";
          break;
        case 500:
          error.value = "Error interno del servidor.";
          break;
        default:
          error.value = err.response.data?.message || "Error inesperado";
      }
    } else {
      error.value = "Error de conexión con el servidor.";
    }

    proxy.$notify(error.value, '', 'danger', 'fa-solid fa-thumbs-down');

  };

  return {
    items,
    currentPage,
    lastPage,
    totalItems,
    itemsPerPage,
    filteredItems,
    error,
    fieldErrors,
    loading,
    fetchItems,
    message,
    reloadRecords,
    addModel,
    editModel,
    deleteRecord,
    confirmDelete,
    restoreRecord,
    changePage,
    applyFilter,
    updateItemsPerPage,
    updateShowDeleted,
    showRecord,
    getRecord,
    setAdditionalParams,
    totalPages,
  };
}
